/** "Documents now" (plan D8): a saved activity row's on-trip documents are managed at
 *  once through their own endpoints — never through the draft — so the action is an
 *  immediate one (bolt icon, "happens at once"). A row the draft creates has nothing to
 *  attach them to yet: "Save first to attach documents". The count is the stored one until
 *  the dialog has loaded the list. */
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ImmediateActionButton } from '@/features/records/actions/ImmediateActionButton';

import { useTourRecord } from '../record/TourRecordContext';

import { ActivityDocumentsDialog, activityDocsKey } from './ActivityDocumentsDialog';
import { useItinerary } from './store';

export function DocumentsNow({ activityId }: { activityId: number }) {
  const { canEdit, baseline } = useItinerary();
  const { detail } = useTourRecord();
  const [open, setOpen] = useState(false);
  const stored = baseline.itinerary.activities.get(activityId)?.documents.length ?? 0;
  const loaded = useQuery({ queryKey: activityDocsKey(activityId), queryFn: () => Promise.resolve({ results: [] }), enabled: false });
  const count = loaded.data ? loaded.data.results.length : stored;
  if (activityId < 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} data-documents="unsaved">
        <AttachFileIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          Save first to attach documents
        </Typography>
      </Box>
    );
  }
  return (
    <>
      <ImmediateActionButton variant="outlined" onClick={() => setOpen(true)} data-documents="saved">
        Documents now{count > 0 ? ` (${count})` : ''}
      </ImmediateActionButton>
      {open && (
        <ActivityDocumentsDialog
          open
          utfId={activityId}
          canManage={canEdit && !detail.is_deleted}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
